import React from "react";
import {
  Box,
  IconButton,
  ListItemIcon,
  ListItemText,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import DarkModeOutlined from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlined from "@mui/icons-material/LightModeOutlined";
import { useColorMode } from "../../contexts/ColorModeContext";

const ICON_SX = { fontSize: 22 } as const;

interface ThemeModeToggleProps {
  /** Sidebar switch row above logout */
  variant?: "nav" | "icon" | "toggle";
  collapsed?: boolean;
}

const ThemeModeToggle: React.FC<ThemeModeToggleProps> = ({
  variant = "icon",
  collapsed = false,
}) => {
  const { mode, setMode, toggleColorMode } = useColorMode();
  const isDark = mode === "dark";
  const label = isDark ? "Dark mode on" : "Light mode on";
  const Icon = isDark ? DarkModeOutlined : LightModeOutlined;

  if (variant === "toggle") {
    const toggleControl = (
      <Switch
        checked={isDark}
        onChange={() => setMode(isDark ? "light" : "dark")}
        size="small"
        inputProps={{ "aria-label": "Toggle dark mode" }}
      />
    );

    if (collapsed) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 0.5 }}>
          <Tooltip title={label} placement="right">
            {toggleControl}
          </Tooltip>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          px: 2,
          py: 0.75,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <Icon sx={{ ...ICON_SX, color: "text.secondary" }} />
          <Typography variant="body2" color="text.secondary">
            Dark mode
          </Typography>
        </Box>
        {toggleControl}
      </Box>
    );
  }

  if (variant === "nav") {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          gap: 1,
          px: collapsed ? 1 : 2,
          py: 0.5,
        }}
      >
        {!collapsed && (
          <>
            <ListItemIcon
              sx={{
                minWidth: 40,
                justifyContent: "center",
                color: "text.secondary",
              }}
            >
              <Icon sx={ICON_SX} />
            </ListItemIcon>
            <ListItemText
              primary="Dark mode"
              primaryTypographyProps={{
                fontSize: "0.9375rem",
                color: "text.secondary",
              }}
              sx={{ flex: 1, m: 0 }}
            />
          </>
        )}
        <Switch
          checked={isDark}
          onChange={() => setMode(isDark ? "light" : "dark")}
          size="small"
          inputProps={{ "aria-label": "Toggle dark mode" }}
        />
      </Box>
    );
  }

  return (
    <Tooltip title={label}>
      <IconButton
        onClick={toggleColorMode}
        aria-label={label}
        sx={{ color: "text.secondary" }}
      >
        <Icon sx={ICON_SX} />
      </IconButton>
    </Tooltip>
  );
};

export default ThemeModeToggle;
